

// VUEX
import { mapGetters, mapActions } from 'vuex';

// COMPONENT
import { directive } from 'vue-awesome-swiper';
import 'swiper/css/swiper.css';

// API
import * as apiProfile from '../api/private/profile';

// Mixins
import errorsMixin from '../mixins/error-handler';

export default {
  name: 'SSwiper',
  directives: {
    swiper: directive,
  },
  mixins: [errorsMixin],
  props: {
    index: {
      type: Number,
      default: 0,
    },
    slides: {
      type: Array,
      required: true,
    },
    enabled: {
      type: Boolean,
      default: true,
    },
    virtual: {
      type: Boolean,
      default: false,
    },
    swiperName: {
      type: String,
      default: null,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    cancelable: {
      type: Boolean,
      default: false,
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Boolean,
      default: false,
    },
    classModifier: {
      type: String,
      default: 'swiper',
    },
    keyboard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      startMovePos: null,
      isSwiping: false,
      showGuideSwipe: false,
      virtualData: {},
      options: {},
    };
  },
  computed: {
    ...mapGetters({
      getWindowWidth: 'screen/getWindowWidth',
      userSecured: 'preferences/userSecured',
      getUserPrefs: 'preferences/getUserPrefs',
    }),
    uniqKey() {
      const listIds = this.slides.map((slide) => slide.id ?? slide.uuid);
      return `${this.isVirtual} - ${this.preview} - ${JSON.stringify(listIds)}`;
    },
    initialSlide() {
      return process.server || this.index >= this.slides.length ? 0 : this.index;
    },
    isVirtual() {
      // slidesPerView doesn't work with virtual
      return this.virtual && this.enabled && !this.preview;
    },
    swiperOptions() {
      return {
        initialSlide: this.initialSlide,
        slidesPerView: this.preview ? 'auto' : 1,
        allowTouchMove: !this.arrow && this.enabled,
        allowSlidePrev: this.enabled,
        allowSlideNext: this.enabled,
        virtual: this.isVirtual ? {
          slides: this.slides,
          renderExternal: (data) => {
            this.virtualData = data;
          },
        } : false,
        spaceBetween: 25,
        centeredSlides: true,
        resistanceRatio: 0.50,
        touchMoveStopPropagation: true,
        pagination: {
          clickable: true,
          dynamicBullets: true,
          el: '.swiper-pagination',
        },
        navigation: {
          prevEl: `.${this.classModifier}-button-prev`,
          nextEl: `.${this.classModifier}-button-next`,
        },
        threshold: 1,
        keyboard: this.keyboard,
      };
    },
  },
  watch: {
    uniqKey() {
      const hasPreviewChange = this.swiperOptions.slidesPerView !== this.$swiper.passedParams.slidesPerView;
      const hasVirtualChange = !!this.swiperOptions.virtual !== this.$swiper.passedParams.virtual.enabled;

      if (hasPreviewChange || hasVirtualChange) {
        this.reloadSwiper(true);
      } else {
        this.reloadSwiper();
      }
    },
    initialSlide(value) {
      if (!this.$swiper.destroyed && value !== this.$swiper.realIndex) {
        this.$swiper.slideTo(value);
        // BUG => wrong slide (5.4.5)
        // this.reloadSwiper();
      }
    },
    getUserPrefs: {
      handler() {
        if (process.client) {
          this.initSwipeGuide();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.initSwiper();
  },
  methods: {
    ...mapActions({
      updateTutoSwiped: 'preferences/updateTutoSwiped',
      initSnackbar: 'snackbar/initSnackbar',
    }),
    // INIT
    initSwiper() {
      this.resetData();
      if (this.isVirtual) {
        // display a slide before renderExternal is called (match SSR)
        this.virtualData.slides = this.slides[this.index] ? [this.slides[this.index]] : [];
        this.virtualData.offset = 0;
      }
    },
    resetData() {
      this.options = this.swiperOptions;
      if (!this.isVirtual) {
        this.virtualData.slides = this.slides;
        this.virtualData.offset = null;
      } else if (this.$swiper && !this.$swiper.destroyed) {
        this.$swiper.virtual.slides = this.slides;
      }
    },
    reloadSwiper(force = false) {
      if (force) {
        this.$swiper?.destroy();
        this.loading = true;
        this.resetData();
        this.$nextTick(() => {
          this.loading = false;
        });
      } else {
        this.resetData();
        this.$swiper.updateSlides();
      }
    },
    // EVENT
    onSlideChange() {
      this.$emit('slide', this.$swiper.realIndex);
    },
    onSliderMove(e) {
      this.isSwiping = true;
      this.$emit('is-swiping', this.isSwiping);
      this.handleCancelSwipe(e);
      this.disableTutoSwipe();
    },
    onTouchEnd() {
      if (this.cancelable) {
        this.startMovePos = null;
        this.unlockSwiper();
      }
      this.isSwiping = false;
      this.$emit('is-swiping', this.isSwiping);
    },
    // HELPERS
    lockSwiper() {
      this.$swiper.allowSlidePrev = false;
      this.$swiper.allowSlideNext = false;
    },
    unlockSwiper() {
      this.$swiper.allowSlidePrev = this.enabled;
      this.$swiper.allowSlideNext = this.enabled;
    },
    handleCancelSwipe(e) {
      if (this.cancelable) {
        const touchX = e.touches[0].clientX;
        if (!this.startMovePos) {
          this.startMovePos = touchX;
        }
        if (this.startMovePos < touchX) {
          this.$emit('cancel', { previous: true });
          this.lockSwiper();
        } else if (this.startMovePos > touchX) {
          this.$emit('cancel', { next: true });
          this.lockSwiper();
        }
      }
    },
    initSwipeGuide() {
      if (this.swiperName && !this.arrow && this.slides.length > 1) {
        this.showGuideSwipe = !this.getUserPrefs.tutoswiper.includes(this.swiperName);
      } else {
        this.showGuideSwipe = false;
      }
    },
    disableTutoSwipe() {
    // Ne plus afficher le guide de swipe
      if (this.showGuideSwipe) {
        this.showGuideSwipe = false;
        this.$emit('show-guide-swipe', this.showGuideSwipe);
        const tutoswiper = [...this.getUserPrefs.tutoswiper];
        tutoswiper.push(this.swiperName);
        this.updateTutoSwiped(tutoswiper);
        if (this.userSecured) {
          clearTimeout(this.timeoutApi);
          this.timeoutApi = setTimeout(() => {
            apiProfile.updatePreference({ tutoswiper }, this).catch((error) => {
            // Validation error
              if (error.response && error.response.status === 400) {
                const snackbar = {
                  options: {
                    color: 'error',
                  },
                  content: this.errors$.get('preference', 'tutoswiper'),
                };
                this.initSnackbar(snackbar);
              }
            });
          }, 1000);
        }
      }
    },
  },
};
